const declaredPatternSteps = (clipData) => Math.max(0, Number(clipData?.patternLength) || 0);

const patternContentSteps = (clipData = {}) => {
  const ppq = Math.max(1, Number(clipData.ppq) || 96);
  const ticksPerStep = ppq / 4;
  let furthestStep = 0;
  (clipData.noteEvents || []).forEach((event) => {
    const endTick = Math.max(0, Number(event?.startTick) || 0)
      + Math.max(1, Number(event?.durationTicks) || ticksPerStep);
    furthestStep = Math.max(furthestStep, Math.ceil(endTick / ticksPerStep));
  });
  Object.entries(clipData.pattern || {}).forEach(([key, active]) => {
    if (!active) return;
    const step = Number(String(key).split('-').pop());
    if (!Number.isFinite(step)) return;
    const duration = Math.max(1, Number(clipData.noteData?.[key]?.durationSteps) || 1);
    furthestStep = Math.max(furthestStep, step + duration);
  });
  return furthestStep;
};

const hasNewContentBeyondStep = (previousData = {}, nextData = {}, visibleSteps = 0) => {
  const previousPattern = previousData.pattern || {};
  const addedGridStep = Object.entries(nextData.pattern || {}).some(([key, active]) => {
    if (!active || previousPattern[key]) return false;
    const step = Number(String(key).split('-').pop());
    return Number.isFinite(step) && step >= visibleSteps;
  });
  if (addedGridStep) return true;

  const eventSignature = (event, index, ppq) => {
    const pitches = Array.isArray(event?.pitches) ? event.pitches : [event?.pitch];
    return [
      event?.id || `index:${index}`,
      Math.round(Number(event?.startTick) || 0),
      Math.max(1, Math.round(Number(event?.durationTicks) || (ppq / 4))),
      pitches.map(Number).filter(Number.isFinite).sort((a, b) => a - b).join(',')
    ].join(':');
  };
  const previousPpq = Math.max(1, Number(previousData.ppq) || 96);
  const nextPpq = Math.max(1, Number(nextData.ppq) || 96);
  const previousEvents = new Set((previousData.noteEvents || []).map((event, index) => (
    eventSignature(event, index, previousPpq)
  )));
  return (nextData.noteEvents || []).some((event, index) => {
    const startStep = (Number(event?.startTick) || 0) / (nextPpq / 4);
    const endStep = startStep + (Math.max(1, Number(event?.durationTicks) || (nextPpq / 4)) / (nextPpq / 4));
    return endStep > visibleSteps && !previousEvents.has(eventSignature(event, index, nextPpq));
  });
};

/**
 * Reveal new source material without undoing a user's non-destructive trim.
 * An unchanged source can remain longer than the visible clip; routine edits
 * must not expose that hidden tail again. The visible boundary grows only when
 * the edit itself increases the declared source length or pushes musical
 * content beyond both its previous content end and the visible clip end.
 */
export function extendPatternClipToData(clip, clipData) {
  if (!clip) return clip;
  const visibleDurationBeats = Math.max(0, Number(clip.durationBeats) || 0);
  const visibleSteps = visibleDurationBeats * 4;
  const previousData = clip.clipData || {};
  const previousDeclaredSteps = declaredPatternSteps(previousData);
  const nextDeclaredSteps = declaredPatternSteps(clipData);
  const previousContentSteps = patternContentSteps(previousData);
  const nextContentSteps = patternContentSteps(clipData);
  const declaredSourceGrew = nextDeclaredSteps > previousDeclaredSteps;
  const contentCrossedVisibleEnd = nextContentSteps > visibleSteps
    && (nextContentSteps > previousContentSteps
      || hasNewContentBeyondStep(previousData, clipData, visibleSteps));
  const newlyRequiredSteps = Math.max(
    declaredSourceGrew ? nextDeclaredSteps : 0,
    contentCrossedVisibleEnd ? nextContentSteps : 0
  );
  const requiredDurationBeats = newlyRequiredSteps > 0
    ? Math.ceil(newlyRequiredSteps / 16) * 4
    : visibleDurationBeats;
  return {
    ...clip,
    durationBeats: Math.max(visibleDurationBeats, requiredDurationBeats),
    clipData
  };
}

/**
 * Grow a pattern only when an edit lands beyond its current visible steps.
 * The final visible step is currentLength - 1 and must never create a silent
 * bar merely because it was edited.
 */
export function patternLengthAfterStepEdit(currentLength, stepIndex, stepsPerBar = 16) {
  const barSize = Math.max(1, Math.round(Number(stepsPerBar) || 16));
  const visibleLength = Math.max(barSize, Math.round(Number(currentLength) || barSize));
  const editedStep = Math.max(0, Math.floor(Number(stepIndex) || 0));
  if (editedStep < visibleLength) return visibleLength;
  return Math.ceil((editedStep + 1) / barSize) * barSize;
}

/**
 * Grow a drum grid when its timeline boundary is extended. Existing note
 * steps stay at their original ticks; the added space is new empty grid time.
 */
export function extendDrumPatternDataToDuration(clipData, durationBeats) {
  if (!clipData || clipData.kind === 'instrument') return clipData;
  const currentLength = Math.max(1, Number(clipData.patternLength) || 16);
  const requiredLength = Math.max(currentLength, Math.ceil(Math.max(0, Number(durationBeats) || 0) * 4));
  return requiredLength > currentLength
    ? { ...clipData, patternLength: requiredLength }
    : clipData;
}

/**
 * Return the end of the last drum clip across independent drum lanes.
 *
 * Instrument MIDI clips also use clipType "pattern", so clip type alone is
 * not enough to decide where a new drum pattern belongs. Audio and instrument
 * lanes must never push drum-pattern creation farther down the timeline.
 */
export function isDrumPatternClip(clip) {
  return Boolean(
    clip?.clipType === 'pattern'
    && clip?.clipData?.kind !== 'instrument'
    && (clip?.trackId === 'pattern-track' || Number.isInteger(clip?.clipData?.drumLaneIndex))
  );
}

export function getNextDrumPatternStartBeat(clips = []) {
  return clips.reduce((nextStartBeat, clip) => {
    if (!isDrumPatternClip(clip)) {
      return nextStartBeat;
    }

    const startBeat = Number(clip.startBeat);
    const durationBeats = Number(clip.durationBeats);
    if (!Number.isFinite(startBeat) || !Number.isFinite(durationBeats)) {
      return nextStartBeat;
    }

    return Math.max(nextStartBeat, Math.max(0, startBeat) + Math.max(0, durationBeats));
  }, 0);
}

/** Resolve either a freshly returned pattern clip or an id from current state. */
export function resolvePatternClipForFocus(clipOrId, clips = []) {
  const clip = clipOrId && typeof clipOrId === 'object'
    ? clipOrId
    : clips.find((candidate) => candidate?.id === clipOrId);
  return clip?.clipType === 'pattern' && clip?.clipData ? clip : null;
}

/** Keep the sequencer focused when a timeline split replaces its selected clip. */
export function resolvePatternClipAfterSplit(selectedClipId, clips = [], playheadBeat = 0) {
  if (!selectedClipId) return null;
  const exact = clips.find(clip => clip?.id === selectedClipId);
  if (exact) return resolvePatternClipForFocus(exact, clips);
  const replacements = clips
    .filter(clip => clip?.splitParentId === selectedClipId && clip?.clipType === 'pattern' && clip?.clipData)
    .sort((a, b) => (Number(a.startBeat) || 0) - (Number(b.startBeat) || 0));
  if (!replacements.length) return null;
  const beat = Number(playheadBeat) || 0;
  return replacements.find(clip => {
    const start = Number(clip.startBeat) || 0;
    const end = start + (Number(clip.durationBeats) || 0);
    return beat >= start && beat < end;
  }) || replacements[0];
}

/** Instrument MIDI is edited exclusively in the piano roll, never the drum sequencer. */
export function resolvePatternEditorView(clip, requestedView = 'step') {
  if (clip?.clipData?.kind === 'instrument') return 'piano';
  return requestedView === 'piano' ? 'piano' : 'step';
}
